import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import CollapseHeader from '../../../../core/common/collapse-header';
import { all_routes } from '../../../router/all_routes';

// DTOs (unchanged)
interface EmployeeDTO {
  name: string;
  empNo: number | null;
  designation: string;
  email: string;
  phone: string;
  status: string | null;
  personType: string;
}
interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface DepartmentDTO {
  id: number;
  name: string;
  location: string;
  address: string;
  createdBy: string;
  createdAt: string;
}
interface VisitorDTO {
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;
  departureTime: string;
}
interface VehicleDTO {
  arrivalTime: string;
  departureTime: string;
  vehicleNumber: string;
}
interface VisitorRequestPayload {
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  locationId: number;
  departmentId: number;
  parkingRequired: boolean;
  dateTime: number;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}
interface GetEmployeesResponse {
  status: string;
  statusDescription: string;
  data: EmployeeDTO[];
  total: number | null;
}
interface GetLocationsResponse {
  status: string;
  data: {
    locations: LocationDTO[];
    total: number;
  };
}
interface GetDepartmentsResponse {
  status: string;
  data: {
    departments: DepartmentDTO[];
    total: number;
  };
}

// ==== Updated Date/Time Helpers (Today + Future Only + 08:00–17:00) ====
const BUSINESS_START = '08:00';
const BUSINESS_END = '17:00';

const pad2 = (n: number) => n.toString().padStart(2, '0');

const todayDateStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

// Get absolute minimum allowed datetime (today 08:00 or tomorrow if past 17:00)
const getMinAllowedDateTime = (): string => {
  const now = new Date();
  const today = todayDateStr();
  const currentTime = now.toTimeString().slice(0, 5); // "HH:MM"

  if (currentTime >= '17:00') {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const y = tomorrow.getFullYear();
    const m = pad2(tomorrow.getMonth() + 1);
    const d = pad2(tomorrow.getDate());
    return `${y}-${m}-${d}T${BUSINESS_START}`;
  }

  return `${today}T${BUSINESS_START}`;
};

// Get min/max bounds for a given datetime-local input value
const getBoundsForInput = (value: string): { min: string; max: string } => {
  const today = todayDateStr();
  const minGlobal = getMinAllowedDateTime();

  if (!value) {
    return { min: minGlobal, max: '' };
  }

  const selectedDate = value.split('T')[0];
  const isToday = selectedDate === today;

  const min = isToday ? minGlobal : `${selectedDate}T${BUSINESS_START}`;
  const max = `${selectedDate}T${BUSINESS_END}`;

  return { min, max };
};

// Clamp value to business hours and min date
const clampToBusinessHours = (dt: string): string => {
  if (!dt) return dt;

  const { min, max } = getBoundsForInput(dt);
  if (dt < min) return min;
  if (dt > max) return max;
  return dt;
};

const isWithinBusinessHours = (dt: string): boolean => {
  if (!dt) return false;
  const { min, max } = getBoundsForInput(dt);
  return dt >= min && dt <= max;
};

const VisitorRequest: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeDTO[]>([]);
  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [departments, setDepartments] = useState<DepartmentDTO[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState<VisitorRequestPayload>({
    responsiblePersonEmpNo: '',
    visitorPosition: '',
    visitorReason: '',
    locationId: 0,
    departmentId: 0,
    parkingRequired: false,
    dateTime: 0,
    visitors: [{
      name: '',
      email: '',
      nic: '',
      phone: '',
      visitorCompany: '',
      arrivalTime: '',
      departureTime: '',
    }],
    vehicles: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Token & Permission Check
  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      showError('Authentication token not found. Please log in.');
      navigate('/login');
      return false;
    }
    return true;
  };

  // Fetch Employees
  useEffect(() => {
    const fetchEmployees = async () => {
      if (!checkToken()) return;
      if (!checkPermission('ROLE_ADD_VISITOR_REQUEST')) {
        showError('You do not have permission to view employees.');
        return;
      }
      setIsLoading(true);
      try {
        let page = 1;
        let hasMore = true;
        const allEmployees: EmployeeDTO[] = [];
        const payload = { filterType: null, filterValue: "", date: null };

        while (hasMore) {
          const response: AxiosResponse = await apiDefinitions.getUsers(page, payload);
          if (response.data.status === "S1000") {
            const usersPage = response.data.data.users || [];
            if (usersPage.length === 0) {
              hasMore = false;
              break;
            }
            const mapped = usersPage
              .filter((user: any) => user.empNumber && ['ACTIVE', 'Active'].includes(user.status))
              .map((user: any) => ({
                name: user.name || "Unknown",
                empNo: user.empNumber ? Number(user.empNumber) : null,
                designation: user.designation || "",
                email: user.email || "",
                phone: user.phone || "",
                status: user.status || "PENDING",
                personType: user.group?.replace("_K", "") || "USER",
              }));
            allEmployees.push(...mapped);
            page++;
          } else {
            showError("Failed to fetch employees: " + response.data.statusDescription);
            break;
          }
        }
        setEmployees(allEmployees);
      } catch (e: any) {
        showError("Failed to fetch employees: " + (e.response?.data?.statusDescription || "Network error"));
        if (e.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // Fetch Locations & Departments (unchanged logic)
  useEffect(() => {
    const fetchLocations = async () => {
      if (!checkToken() || !checkPermission('ROLE_ADD_VISITOR_REQUEST')) return;
      setIsLoading(true);
      try {
        const res = await apiDefinitions.getLocations('1', { filterType: '', filterValue: '', date: '' });
        if (res.status === 200) setLocations(res.data.data.locations || []);
      } catch (e: any) {
        if (e.response?.status === 401) navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };
    fetchLocations();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!checkToken() || !checkPermission('ROLE_ADD_VISITOR_REQUEST')) return;
      setIsLoading(true);
      try {
        const res = await apiDefinitions.getDepartments('1', { filterType: 'NAME', filterValue: '' });
        if (res.status === 200) setDepartments(res.data.data.departments || []);
      } catch (e: any) {
        if (e.response?.status === 401) navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDepartments();
  }, []);

  const filteredEmployees = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return employees
      .filter(emp => 
        (emp.name.toLowerCase().includes(query) || 
         (emp.empNo && String(emp.empNo).includes(query))) &&
        emp.status === 'ACTIVE'
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, searchQuery]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof VisitorRequestPayload,
    index?: number,
    subField?: keyof VisitorDTO | keyof VehicleDTO
  ) => {
    const rawValue = e.target.value;

    setFormData(prev => {
      if (index !== undefined && subField) {
        if (field === 'visitors') {
          const updated = [...prev.visitors];
          const clamped = (subField === 'arrivalTime' || subField === 'departureTime')
            ? clampToBusinessHours(rawValue)
            : rawValue;
          updated[index] = { ...updated[index], [subField]: clamped };
          return { ...prev, visitors: updated };
        }
        if (field === 'vehicles') {
          const updated = [...prev.vehicles];
          const clamped = (subField === 'arrivalTime' || subField === 'departureTime')
            ? clampToBusinessHours(rawValue)
            : rawValue;
          updated[index] = { ...updated[index], [subField]: clamped };
          return { ...prev, vehicles: updated };
        }
      } else if (field === 'parkingRequired') {
        return { ...prev, parkingRequired: rawValue === 'true' };
      } else if (field === 'locationId' || field === 'departmentId') {
        return { ...prev, [field]: Number(rawValue) };
      } else {
        return { ...prev, [field]: rawValue };
      }
      return prev;
    });
  };

  const addVisitor = () => setFormData(prev => ({
    ...prev,
    visitors: [...prev.visitors, {
      name: '', email: '', nic: '', phone: '', visitorCompany: '',
      arrivalTime: '', departureTime: ''
    }]
  }));

  const addVehicle = () => setFormData(prev => ({
    ...prev,
    vehicles: [...prev.vehicles, { vehicleNumber: '', arrivalTime: '', departureTime: '' }]
  }));

  const removeVisitor = (i: number) => setFormData(prev => ({
    ...prev, visitors: prev.visitors.filter((_, idx) => idx !== i)
  }));

  const removeVehicle = (i: number) => setFormData(prev => ({
    ...prev, vehicles: prev.vehicles.filter((_, idx) => idx !== i)
  }));

  const validateForm = () => {
    if (!formData.responsiblePersonEmpNo) return setError('Please select a responsible person.'), false;
    if (!formData.locationId) return setError('Please select a location.'), false;
    if (!formData.departmentId) return setError('Please select a department.'), false;
    if (!formData.visitorPosition) return setError('Please enter visitor position.'), false;
    if (!formData.visitorReason) return setError('Please enter visitor reason.'), false;
    if (formData.visitors.length === 0 || !formData.visitors[0].name) return setError('Add at least one visitor.'), false;

    for (const v of formData.visitors) {
      if (!v.arrivalTime || !v.departureTime) return setError('All visitors need arrival & departure times.'), false;
      if (!isWithinBusinessHours(v.arrivalTime) || !isWithinBusinessHours(v.departureTime))
        return setError('Visitor times must be between 08:00 and 17:00.'), false;
    }

    if (formData.parkingRequired && formData.vehicles.length === 0)
      return setError('Add at least one vehicle if parking is required.'), false;

    for (const veh of formData.vehicles) {
      if (!veh.vehicleNumber || !veh.arrivalTime || !veh.departureTime)
        return setError('All vehicles need number and times.'), false;
      if (!isWithinBusinessHours(veh.arrivalTime) || !isWithinBusinessHours(veh.departureTime))
        return setError('Vehicle times must be between 08:00 and 17:00.'), false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkToken() || !checkPermission('ROLE_ADD_VISITOR_REQUEST')) return;
    if (!validateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        dateTime: new Date(formData.visitors[0].arrivalTime).getTime(),
      };

      const res = await apiDefinitions.addVisitor(payload);
      if (res.status === 200) {
        showSuccess('Visitor request added successfully');
        setFormData({
          responsiblePersonEmpNo: '',
          visitorPosition: '',
          visitorReason: '',
          locationId: 0,
          departmentId: 0,
          parkingRequired: false,
          dateTime: 0,
          visitors: [{ name: '', email: '', nic: '', phone: '', visitorCompany: '', arrivalTime: '', departureTime: '' }],
          vehicles: [],
        });
        setSearchQuery('');
      } else {
        showError(res.data?.statusDescription || 'Failed to submit request');
      }
    } catch (err: any) {
      showError(err.response?.data?.statusDescription || 'Submission failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <style>{`
        .form-card { border-radius: .75rem; }
        .form-card .card-header { background: var(--bs-primary-subtle); }
        .visitor-section, .vehicle-section { border: 1px solid var(--bs-border-color); border-radius: .5rem; padding: 1rem; margin-bottom: 1rem; }
        .remove-btn { cursor: pointer; font-size: 1.2rem; }
        .remove-btn:hover { color: var(--bs-danger); }
        .employee-search { position: relative; }
        .employee-search input { padding-right: 2.5rem; }
        .employee-search i { position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); pointer-events: none; }
      `}</style>

      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Visitor Management / Visitor Request</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons"><CollapseHeader /></div>
                </div>
              </div>
            </div>

            <div className="card form-card">
              <div className="card-header">
                <h5 className="card-title">Add Visitor Request</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">

                    {/* Responsible Person */}
                    <div className="col-12 col-md-6">
                      <label className="form-label">Responsible Person <span className="text-danger">*</span></label>
                      <div className="employee-search">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search name or emp no..."
                          value={searchQuery}
                          onChange={e => setSearchQuery(e.target.value)}
                          disabled={isLoading}
                        />
                        <i className="ti ti-search"></i>
                      </div>
                      <select
                        className="form-select mt-2"
                        value={formData.responsiblePersonEmpNo}
                        onChange={e => handleInputChange(e, 'responsiblePersonEmpNo')}
                        required
                        disabled={isLoading}
                      >
                        <option value="">Select employee</option>
                        {filteredEmployees.map(emp => (
                          <option key={emp.empNo} value={emp.empNo!}>
                            {emp.name} ({emp.empNo})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Location & Department */}
                    <div className="col-12 col-md-6">
                      <label className="form-label">Location <span className="text-danger">*</span></label>
                      <select className="form-select" value={formData.locationId || ''} onChange={e => handleInputChange(e, 'locationId')} required disabled={isLoading}>
                        <option value="">Select location</option>
                        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Department <span className="text-danger">*</span></label>
                      <select className="form-select" value={formData.departmentId || ''} onChange={e => handleInputChange(e, 'departmentId')} required disabled={isLoading}>
                        <option value="">Select department</option>
                        {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    </div>

                    {/* Position & Reason */}
                    <div className="col-12 col-md-6">
                      <label className="form-label">Visitor Position <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" value={formData.visitorPosition} onChange={e => handleInputChange(e, 'visitorPosition')} required disabled={isLoading} />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Visitor Reason <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" value={formData.visitorReason} onChange={e => handleInputChange(e, 'visitorReason')} required disabled={isLoading} />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Parking Required</label>
                      <select className="form-select" value={formData.parkingRequired.toString()} onChange={e => handleInputChange(e, 'parkingRequired')} disabled={isLoading}>
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>

                    {/* Visitors */}
                    <div className="col-12">
                      <h6 className="mt-4 mb-3">Visitors</h6>
                      {formData.visitors.map((visitor, idx) => {
                        const timeValue = visitor.arrivalTime || visitor.departureTime || '';
                        const { min, max } = getBoundsForInput(timeValue);

                        return (
                          <div key={idx} className="visitor-section">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6>Visitor {idx + 1}</h6>
                              {formData.visitors.length > 1 && (
                                <i className="ti ti-trash remove-btn" onClick={() => removeVisitor(idx)}></i>
                              )}
                            </div>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label>Name <span className="text-danger">*</span></label>
                                <input type="text" className="form-control" value={visitor.name} onChange={e => handleInputChange(e, 'visitors', idx, 'name')} required disabled={isLoading} />
                              </div>
                              <div className="col-md-6">
                                <label>Email</label>
                                <input type="email" className="form-control" value={visitor.email} onChange={e => handleInputChange(e, 'visitors', idx, 'email')} disabled={isLoading} />
                              </div>
                              <div className="col-md-6">
                                <label>NIC</label>
                                <input type="text" className="form-control" value={visitor.nic} onChange={e => handleInputChange(e, 'visitors', idx, 'nic')} disabled={isLoading} />
                              </div>
                              <div className="col-md-6">
                                <label>Phone</label>
                                <input type="text" className="form-control" value={visitor.phone} onChange={e => handleInputChange(e, 'visitors', idx, 'phone')} disabled={isLoading} />
                              </div>
                              <div className="col-md-6">
                                <label>Company</label>
                                <input type="text" className="form-control" value={visitor.visitorCompany} onChange={e => handleInputChange(e, 'visitors', idx, 'visitorCompany')} disabled={isLoading} />
                              </div>
                              <div className="col-md-6">
                                <label>Arrival Time <span className="text-danger">*</span></label>
                                <input
                                  type="datetime-local"
                                  className="form-control"
                                  value={visitor.arrivalTime}
                                  onChange={e => handleInputChange(e, 'visitors', idx, 'arrivalTime')}
                                  min={min}
                                  max={max}
                                  step="300"
                                  required
                                  disabled={isLoading}
                                />
                                <small className="text-muted">08:00 – 17:00 only</small>
                              </div>
                              <div className="col-md-6">
                                <label>Departure Time <span className="text-danger">*</span></label>
                                <input
                                  type="datetime-local"
                                  className="form-control"
                                  value={visitor.departureTime}
                                  onChange={e => handleInputChange(e, 'visitors', idx, 'departureTime')}
                                  min={min}
                                  max={max}
                                  step="300"
                                  required
                                  disabled={isLoading}
                                />
                                <small className="text-muted">08:00 – 17:00 only</small>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addVisitor} disabled={isLoading}>
                        Add Another Visitor
                      </button>
                    </div>

                    {/* Vehicles (if parking required) */}
                    {formData.parkingRequired && (
                      <div className="col-12">
                        <h6 className="mt-4 mb-3">Vehicles</h6>
                        {formData.vehicles.map((veh, idx) => {
                          const timeValue = veh.arrivalTime || veh.departureTime || '';
                          const { min, max } = getBoundsForInput(timeValue);

                          return (
                            <div key={idx} className="vehicle-section">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6>Vehicle {idx + 1}</h6>
                                <i className="ti ti-trash remove-btn" onClick={() => removeVehicle(idx)}></i>
                              </div>
                              <div className="row g-3">
                                <div className="col-md-4">
                                  <label>Vehicle Number <span className="text-danger">*</span></label>
                                  <input type="text" className="form-control" value={veh.vehicleNumber} onChange={e => handleInputChange(e, 'vehicles', idx, 'vehicleNumber')} required disabled={isLoading} />
                                </div>
                                <div className="col-md-4">
                                  <label>Arrival Time <span className="text-danger">*</span></label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={veh.arrivalTime}
                                    onChange={e => handleInputChange(e, 'vehicles', idx, 'arrivalTime')}
                                    min={min}
                                    max={max}
                                    step="300"
                                    required
                                    disabled={isLoading}
                                  />
                                </div>
                                <div className="col-md-4">
                                  <label>Departure Time <span className="text-danger">*</span></label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={veh.departureTime}
                                    onChange={e => handleInputChange(e, 'vehicles', idx, 'departureTime')}
                                    min={min}
                                    max={max}
                                    step="300"
                                    required
                                    disabled={isLoading}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addVehicle} disabled={isLoading}>
                          Add Another Vehicle
                        </button>
                      </div>
                    )}
                  </div>

                  {error && <div className="alert alert-danger mt-4">{error}</div>}

                  <div className="mt-4 text-end">
                    <button type="button" className="btn btn-outline-primary me-2" onClick={() => navigate(all_routes.viewrooms)} disabled={isLoading}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success" disabled={isLoading}>
                      {isLoading ? 'Submitting...' : 'Submit Request'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorRequest;